"use client";

import { ApolloError } from "@apollo/client";
import { Component, ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "../rac/button";

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	isConnectionError: boolean;
}

export class ApolloErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			isConnectionError: false,
		};
	}

	static getDerivedStateFromError(error: Error): State {
		// Check if it's an Apollo connection error
		const isConnectionError =
			error instanceof ApolloError ||
			error.message.includes("fetch") ||
			error.message.includes("NetworkError") ||
			error.message.includes("timeout") ||
			error.message.includes("AbortError") ||
			error.message.includes("Failed to connect");

		return {
			hasError: true,
			error,
			isConnectionError,
		};
	}

	componentDidCatch(error: Error, errorInfo: unknown) {
		// eslint-disable-next-line no-console
		console.error("Apollo Error Boundary caught an error:", error, errorInfo);

		// Show appropriate toast message
		if (this.state.isConnectionError) {
			toast.error("Connection failed. Please check your internet connection and try again.", {
				duration: 5000,
				action: {
					label: "Retry",
					onClick: () => window.location.reload(),
				},
			});
		} else {
			toast.error("Something went wrong. Please try again.", {
				duration: 5000,
				action: {
					label: "Retry",
					onClick: () => window.location.reload(),
				},
			});
		}
	}

	handleRetry = () => {
		this.setState({
			hasError: false,
			error: null,
			isConnectionError: false,
		});
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-gray-50">
					<div className="max-w-md w-full mx-4">
						<div className="bg-white rounded-lg shadow-lg p-6 text-center">
							<div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
								<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
									/>
								</svg>
							</div>

							<h2 className="text-xl font-semibold text-gray-900 mb-2">
								{this.state.isConnectionError ? "Connection Failed" : "Something went wrong"}
							</h2>

							<p className="text-gray-600 mb-6">
								{this.state.isConnectionError
									? "Unable to connect to the server. Please check your internet connection and try again."
									: "An unexpected error occurred. Please try refreshing the page."}
							</p>

							<div className="space-y-3">
								<Button onPress={this.handleRetry} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
									Try Again
								</Button>

								<Button onPress={() => (window.location.href = "/")} variant="outline" className="w-full">
									Go to Homepage
								</Button>
							</div>

							{process.env.NODE_ENV === "development" && this.state.error && (
								<details className="mt-4 text-left">
									<summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
										Error Details (Development)
									</summary>
									<pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
										{this.state.error.stack || this.state.error.message}
									</pre>
								</details>
							)}
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
